import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Tips = ({ language }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const tips = language === 'zh' ? [
    {
      icon: FiIcons.FiHeart,
      title: '💝 慶祝特殊里程碑',
      content: '100天、520天、1314天等特殊日子值得特別慶祝！我們的計算器會自動追蹤這些重要時刻。',
      keywords: ['100天紀念日', '520天', '1314天', '愛情里程碑', '特殊紀念日']
    },
    {
      icon: FiIcons.FiImage,
      title: '🎨 生成精美紀念圖片',
      content: '使用我們的圖片生成功能，創建9種不同風格的紀念圖片，完美適合在Instagram、Facebook等社交媒體分享！',
      keywords: ['紀念圖片生成', 'Instagram分享', '社交媒體', '紀念日圖片', '愛情圖片']
    },
    {
      icon: FiIcons.FiCalendar,
      title: '📅 多種紀念類型',
      content: '支持戀愛、結婚、相識、初次約會、求婚等多種紀念日類型，記錄你們愛情旅程的每個重要時刻。',
      keywords: ['戀愛紀念日', '結婚紀念日', '相識紀念日', '約會紀念', '求婚紀念']
    },
    {
      icon: FiIcons.FiClock,
      title: '⏰ 工作日計算功能',
      content: '需要計算項目期限或合約時間？使用我們的工作日計算器，自動排除週末，精確計算工作天數。',
      keywords: ['工作日計算器', '工作天數', '項目期限', '合約計算', '營業日計算']
    },
    {
      icon: FiIcons.FiAward,
      title: '🏆 解鎖愛情成就',
      content: '隨著你們在一起的時間增長，解鎖各種愛情成就徽章，從「新手情侶」到「鑽石愛情」，見證你們的愛情成長！',
      keywords: ['愛情成就', '關係徽章', '情侶成就', '愛情等級', '紀念徽章']
    },
    {
      icon: FiIcons.FiShare2,
      title: '📱 輕鬆分享',
      content: '一鍵分享你們的愛情時光到Facebook、Twitter、WhatsApp等平台，與親朋好友一起慶祝你們的幸福！',
      keywords: ['分享功能', 'Facebook分享', 'WhatsApp分享', '社交分享', '愛情分享']
    },
    {
      icon: FiIcons.FiTrendingUp,
      title: '📊 詳細時間統計',
      content: '不只是天數！查看精確的年數、月數、小時數，甚至分鐘數，全面了解你們在一起的每一刻。',
      keywords: ['時間統計', '精確計算', '詳細分析', '愛情時間', '關係統計']
    },
    {
      icon: FiIcons.FiGlobe,
      title: '🌏 多語言支持',
      content: '支持繁體中文和英文雙語界面，無論你在哪裡，都能輕鬆使用我們的工具記錄美好時光。',
      keywords: ['多語言', '中英文', '繁體中文', '英文版', '語言切換']
    }
  ] : [
    {
      icon: FiIcons.FiHeart,
      title: '💝 Celebrate Special Milestones',
      content: 'Special days like 100 days, 520 days, 1314 days deserve special celebration! Our calculator automatically tracks these important moments.',
      keywords: ['100 days anniversary', '520 days', '1314 days', 'love milestones', 'special anniversaries']
    },
    {
      icon: FiIcons.FiImage,
      title: '🎨 Generate Beautiful Images',
      content: 'Use our image generation feature to create 9 different style anniversary images, perfect for sharing on Instagram, Facebook and other social media!',
      keywords: ['anniversary image generator', 'Instagram sharing', 'social media', 'love images', 'relationship pictures']
    },
    {
      icon: FiIcons.FiCalendar,
      title: '📅 Multiple Anniversary Types',
      content: 'Support for dating, wedding, first meeting, first date, proposal and more anniversary types to record every important moment in your love journey.',
      keywords: ['dating anniversary', 'wedding anniversary', 'first meeting', 'first date', 'proposal anniversary']
    },
    {
      icon: FiIcons.FiClock,
      title: '⏰ Working Days Calculator',
      content: 'Need to calculate project deadlines or contract periods? Use our working days calculator to automatically exclude weekends and precisely count business days.',
      keywords: ['working days calculator', 'business days', 'project deadline', 'contract calculation', 'workday counter']
    },
    {
      icon: FiIcons.FiAward,
      title: '🏆 Unlock Love Achievements',
      content: 'As your time together grows, unlock various love achievement badges from "Rookie Couple" to "Diamond Love", witnessing your love story!',
      keywords: ['love achievements', 'relationship badges', 'couple achievements', 'love levels', 'anniversary badges']
    },
    {
      icon: FiIcons.FiShare2,
      title: '📱 Easy Sharing',
      content: 'Share your love journey to Facebook, Twitter, WhatsApp and more platforms with one click, celebrating your happiness with friends and family!',
      keywords: ['share feature', 'Facebook sharing', 'WhatsApp sharing', 'social sharing', 'love sharing']
    },
    {
      icon: FiIcons.FiTrendingUp,
      title: '📊 Detailed Time Statistics',
      content: 'More than just days! View precise years, months, hours, even minutes to fully understand every moment you\'ve been together.',
      keywords: ['time statistics', 'precise calculation', 'detailed analysis', 'love time', 'relationship stats']
    },
    {
      icon: FiIcons.FiGlobe,
      title: '🌏 Multilingual Support',
      content: 'Support for Traditional Chinese and English interfaces. No matter where you are, easily use our tool to record beautiful moments.',
      keywords: ['multilingual', 'Chinese English', 'Traditional Chinese', 'English version', 'language switch']
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentTipIndex((prev) => (prev + 1) % tips.length);
        setIsVisible(true);
      }, 300);
    }, 8000); // Change tip every 8 seconds

    return () => clearInterval(interval);
  }, [tips.length]);

  const currentTip = tips[currentTipIndex];

  return (
    <div className="bg-gradient-to-r from-love-50 to-secondary-50 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 shadow-md border border-love-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-love-600 dark:text-love-400 flex items-center">
          <SafeIcon icon={FiIcons.FiInfo} className="mr-2" />
          {language === 'zh' ? '💡 使用技巧' : '💡 Tips & Tricks'}
        </h2>
        <div className="flex gap-1">
          {tips.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => {
                  setCurrentTipIndex(index);
                  setIsVisible(true);
                }, 300);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentTipIndex
                  ? 'bg-love-500 w-6'
                  : 'bg-love-200 dark:bg-gray-600 hover:bg-love-300'
              }`}
              aria-label={`Go to tip ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key={currentTipIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-3"
          >
            <div className="flex items-center gap-3">
              <div className="bg-love-100 dark:bg-love-900 p-3 rounded-full">
                <SafeIcon icon={currentTip.icon} className="text-love-600 dark:text-love-400 text-xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                {currentTip.title}
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              {currentTip.content}
            </p>

            {/* SEO Keywords (hidden but indexed) */}
            <div className="sr-only" aria-hidden="true">
              {currentTip.keywords.join(', ')}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-4 flex gap-2">
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => {
              setCurrentTipIndex((prev) => (prev === 0 ? tips.length - 1 : prev - 1));
              setIsVisible(true);
            }, 300);
          }}
          className="p-2 rounded-lg bg-white dark:bg-gray-700 hover:bg-love-50 dark:hover:bg-gray-600 transition-colors"
          aria-label="Previous tip"
        >
          <SafeIcon icon={FiIcons.FiChevronLeft} className="text-love-600 dark:text-love-400" />
        </button>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => {
              setCurrentTipIndex((prev) => (prev + 1) % tips.length);
              setIsVisible(true);
            }, 300);
          }}
          className="p-2 rounded-lg bg-white dark:bg-gray-700 hover:bg-love-50 dark:hover:bg-gray-600 transition-colors"
          aria-label="Next tip"
        >
          <SafeIcon icon={FiIcons.FiChevronRight} className="text-love-600 dark:text-love-400" />
        </button>
      </div>
    </div>
  );
};

export default Tips;